/**
 * parent/children/$studentId/quiz/$attemptId.tsx — Parent's
 * read-only view of a single child attempt (Phase H.4).
 *
 * Calls GET /api/quiz/attempt/:id which the existing
 * canViewAttempt handles via isParent=true (gated by the
 * parent_student_links approved row at the API layer in
 * Phase E.2).
 *
 * Renders the same per-question breakdown as the student
 * result page. The studentId is purely a path param so the
 * URL reads naturally (parent/children/:studentId/quiz/...)
 * — the API uses just :attemptId for the auth check.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { createFileRoute, Link, redirect, useParams } from '@tanstack/react-router';
import { PageHeader } from '../../../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../../../components/ui/StatePanel.js';
import { freshSessionQuery } from '../../../../../lib/api/queries.js';

export const Route = createFileRoute('/parent/children/$studentId/quiz/$attemptId')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role !== 'parent') {
      // Non-parents shouldn't be viewing a child's quiz.
      throw redirect({ to: user.role === 'admin' ? '/admin' : '/' });
    }
    return null;
  },
  component: ParentAttemptDetailPage,
});

interface AttemptDetail {
  attempt: {
    id: string;
    activationId: string;
    totalQuestions: number;
    totalCorrect: number;
    starsEarned: number;
    percentage: number;
    status: string;
  };
  quiz: { id: string; title: string; description: string | null } | null;
  answers: Array<{
    questionId: string;
    prompt: string;
    type: string;
    options: string[] | null;
    correctAnswer: string;
    selected: string;
    isCorrect: boolean;
    starEarned: number;
    points: number;
  }>;
}

function ParentAttemptDetailPage(): ReactNode {
  const { studentId, attemptId } = useParams({ strict: false }) as { studentId: string; attemptId: string };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AttemptDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/quiz/attempt/${encodeURIComponent(attemptId)}`, { credentials: 'include' });
        const j = (await r.json()) as { ok: boolean; data?: AttemptDetail; error?: { message: string } };
        if (cancelled) return;
        if (!j.ok || !j.data) {
          setLoadError(j.error?.message ?? 'Gagal memuat attempt');
          setLoading(false);
          return;
        }
        setDetail(j.data);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError((err as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attemptId]);

  if (loading) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz Anak" title="Memuat…" />
      </main>
    );
  }
  if (loadError || !detail) {
    return (
      <main className="page-shell">
        <PageHeader eyebrow="Quiz Anak" title="Attempt tidak ditemukan" />
        <StatePanel variant="error" title="Gagal memuat" description={loadError ?? 'Tidak ditemukan'} />
      </main>
    );
  }

  const correct = detail.answers.filter((a) => a.isCorrect).length;
  const stars = detail.answers.reduce((s, a) => s + a.starEarned, 0);

  return (
    <main className="page-shell" data-testid="parent-attempt">
      <PageHeader
        eyebrow="Quiz Anak"
        title={detail.quiz?.title ?? 'Quiz'}
        description={`${correct} / ${detail.attempt.totalQuestions} benar · ${stars}⭐ · ${detail.attempt.percentage}%`}
        action={
          <Link
            to="/parent/children/$studentId"
            params={{ studentId }}
            className="button button-secondary"
          >
            ← Kembali ke progres anak
          </Link>
        }
      />

      <section className="section">
        <h2 className="section-title">Ringkasan</h2>
        <ul className="metric-row">
          <li className="card">
            <p className="section-eyebrow">Benar</p>
            <p className="metric-value">{correct} / {detail.attempt.totalQuestions}</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Bintang</p>
            <p className="metric-value">{stars}⭐</p>
          </li>
          <li className="card">
            <p className="section-eyebrow">Persentase</p>
            <p className="metric-value">{detail.attempt.percentage}%</p>
          </li>
        </ul>
      </section>

      <section className="section">
        <h2 className="section-title">Per soal</h2>
        {detail.answers.length === 0 ? (
          <StatePanel variant="empty" title="Belum ada soal dijawab" />
        ) : (
          <ol className="preview-question-list" data-testid="parent-result-questions">
            {detail.answers.map((a, i) => (
              <li key={a.questionId} className="card question-editor">
                <header className="question-editor-header">
                  <h4 className="question-editor-title">
                    Soal {i + 1} · {a.isCorrect ? '✅ Benar' : '❌ Kurang tepat'}
                  </h4>
                  <span className="card-meta">
                    {a.starEarned} / {a.points}⭐
                  </span>
                </header>
                <p className="preview-prompt">{a.prompt}</p>
                {a.type === 'true_false' ? (
                  <ul className="preview-options">
                    {(['true', 'false'] as const).map((v) => {
                      const isUser = a.selected === v;
                      const isCorrect = a.correctAnswer === v;
                      return (
                        <li
                          key={v}
                          className={`preview-option ${isCorrect ? 'is-correct' : ''} ${isUser && !isCorrect ? 'is-wrong' : ''}`}
                        >
                          {v === 'true' ? 'Benar' : 'Salah'}
                          {isCorrect ? ' ✓ jawaban benar' : ''}
                          {isUser ? ' (pilihan anak)' : ''}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul className="preview-options">
                    {a.options?.map((opt, j) => {
                      const idStr = String(j);
                      const isUser = a.selected === idStr || (a.type === 'multi_select' && a.selected.split(',').includes(idStr));
                      const isCorrect = a.correctAnswer === idStr || (a.type === 'multi_select' && a.correctAnswer.split(',').includes(idStr));
                      return (
                        <li
                          key={j}
                          className={`preview-option ${isCorrect ? 'is-correct' : ''} ${isUser && !isCorrect ? 'is-wrong' : ''}`}
                        >
                          <span className="preview-option-marker">{String.fromCharCode(65 + j)}.</span>
                          <span>{opt}</span>
                          {isCorrect ? <span className="form-help"> ✓ benar</span> : null}
                          {isUser ? <span className="form-help"> (anak)</span> : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>
    </main>
  );
}
